import { DateTime } from 'luxon'
// utils/markupParser.js
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import linkifyHtml from 'linkify-html'

import html2canvas from 'html2canvas'

const formatTimestamp = timestamp => {
  if (timestamp) {
    let formattedDiff
    let timeFormat = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }
    const dateObj = new Date(timestamp)

    const start = DateTime.fromJSDate(dateObj)
    const end = DateTime.now()
    const diffInMinutes = end.diff(start, 'minutes').toObject()
    const diffInHours = end.diff(start, 'hours').toObject()
    const diffInDays = end.diff(start, 'days').toObject()

    if (diffInMinutes.minutes <= 60) {
      formattedDiff = parseInt(diffInMinutes.minutes) + 'm'
    } else if (diffInHours.hours <= 24) {
      formattedDiff = parseInt(diffInHours.hours) + 'h'
    } else if (diffInDays.days <= 30) {
      timeFormat.month = 'short'
      timeFormat.day = 'numeric'
      formattedDiff = parseInt(diffInDays.days) + 'd'
    } else {
      timeFormat.month = 'short'
      timeFormat.day = 'numeric'
      timeFormat.year = 'numeric'
    }

    const formattedTimestamp = dateObj.toLocaleString('en-US', timeFormat)
    return formattedDiff
      ? formattedTimestamp + ` (${formattedDiff} ago)`
      : formattedTimestamp
  } else {
    return ''
  }
}

const getCookieOptions = ttl => {
  if (!ttl || ttl < 0) ttl = 2592000
  var date = new Date()
  date.setTime(date.getTime() + ttl * 1000)
  return {
    samesite: 'None',
    expires: date,
    secure: true,
  }
}

// https://boomtown.atlassian.net/browse/DEV-4787
// dates from GET v2/issues are in PST, but do not have a TZ in the string
// use Luxon to create a date with the correct TZ
// UPDATE - dates now in UTC, now adding TZ to the string
const addTZtoDateTimeString = date => {
  // Convert "2025-01-17 04:42:33" to "2025-01-17T04:42:33Z"
  const isoString = date.replace(' ', 'T') + 'Z'
  return DateTime.fromISO(isoString)
}

// Function to post-process links
function addTargetBlankToLinks(html) {
  const div = document.createElement('div')
  div.innerHTML = html
  const links = div.querySelectorAll('a')
  links.forEach(link => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })
  return div.innerHTML
}

// Protect bare URLs so markdown emphasis cannot rewrite underscores inside them.
function protectUrls(text) {
  const urls = []
  const protectedText = text.replace(/https?:\/\/[^\s<]+/gi, match => {
    let url = match
    let trailing = ''
    // Keep common trailing punctuation outside the URL
    while (/[.,;:!?)]+$/.test(url)) {
      trailing = url.slice(-1) + trailing
      url = url.slice(0, -1)
    }
    const token = `URLPLACEHOLDER${urls.length}Z`
    urls.push(url)
    return `${token}${trailing}`
  })
  return { text: protectedText, urls }
}

function restoreUrls(html, urls) {
  return urls.reduce((result, url, index) => {
    return result.split(`URLPLACEHOLDER${index}Z`).join(url)
  }, html)
}

// Escape single underscores so markdown-it won't treat them as italics.
// Leaves __bold__ intact. Matches cxme-ui chat (asterisk-only italics).
function escapeSingleUnderscores(text) {
  return text.replace(/(?<![\\_])_(?!_)/g, '\\_')
}

function parseMarkup(input) {
  if (!input) return input
  
  // Helper function to check if text contains HTML tags
  const containsHTML = text => {
    return /<[^>]+>/.test(text)
  }
  
  // Helper function to check if text contains markdown
  // Do not treat single `_` as markdown — it appears in URLs and identifiers.
  const containsMarkdown = text => {
    const markdownPatterns = [
      /[*~`]/, // Asterisk emphasis/bold, strikethrough, code
      /__/, // Underscore bold only (not single-underscore italics)
      /^\s*[-+*]\s/m, // List items
      /^\s*#{1,6}\s/m, // Headers
      /\[.*?\]\(.*?\)/, // Links
      /^\s*>\s/m, // Blockquotes
      /^\s*```/m, // Code blocks
    ]
    return markdownPatterns.some(pattern => pattern.test(text))
  }

  const hasHTML = containsHTML(input)
  const hasMarkdown = containsMarkdown(input)

  // If input contains both HTML and markdown (like XMPP messages with <br> and **bold**)
  if (hasHTML && hasMarkdown) {
    // First, convert inline markdown to HTML while preserving existing HTML tags
    let processed = input
    
    // Convert bold: **text** or __text__
    processed = processed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    processed = processed.replace(/__(.+?)__/g, '<strong>$1</strong>')
    
    // Convert italic: *text* only (not _text_ — breaks URLs with underscores)
    processed = processed.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>')
    
    // Convert inline code: `code`
    processed = processed.replace(/`([^`]+)`/g, '<code>$1</code>')
    
    // Convert links: [text](url)
    processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    
    // Sanitize and linkify
    const purifiedHtml = DOMPurify.sanitize(processed, {
      ALLOWED_TAGS: ['br', 'b', 'i', 'strong', 'em', 'a', 'p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote']
    })
    const linkifiedHtml = addTargetBlankToLinks(purifiedHtml)
    return linkifiedHtml
  } 
  // If input contains only HTML (like <br> tags from XMPP), sanitize and linkify
  else if (hasHTML) {
    const purifiedHtml = DOMPurify.sanitize(input, {
      ALLOWED_TAGS: ['br', 'b', 'i', 'strong', 'em', 'a', 'p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote']
    })
    const linkifiedHtml = addTargetBlankToLinks(purifiedHtml)
    return linkifiedHtml
  } 
  // If it contains only markdown, parse it with MarkdownIt
  else if (hasMarkdown) {
    const markdownParser = new MarkdownIt({ linkify: true })
    // Pull URLs out before markdown so underscore emphasis cannot corrupt them,
    // then escape remaining single underscores (snake_case, etc.).
    const { text: protectedText, urls } = protectUrls(input)
    const html = markdownParser.render(escapeSingleUnderscores(protectedText))
    const restored = restoreUrls(html, urls)
    const linkified = linkifyHtml(restored, {
      attributes: { target: '_blank', rel: 'noopener noreferrer' }
    })
    const purifiedHtml = DOMPurify.sanitize(linkified)
    const linkifiedHtml = addTargetBlankToLinks(purifiedHtml)
    return linkifiedHtml
  } 
  // Plain text - just linkify URLs
  else {
    const linkified = linkifyHtml(input, {
      attributes: {
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    })
    const purifiedHtml = DOMPurify.sanitize(linkified)
    const linkifiedHtml = addTargetBlankToLinks(purifiedHtml)
    return linkifiedHtml
  }
}

// Streaming-specific markup parser that handles partial content gracefully
function parseStreamingMarkup(input) {
  if (!input) return input

  // For streaming, we need to handle incomplete markdown more gracefully
  // Convert basic markdown formatting that's likely to be complete
  let html = input

  // Convert headers (only if line is complete)
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')

  // Convert bold/italic only if properly closed
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>')

  // Convert line breaks
  html = html.replace(/\n/g, '<br>')
  
  // Handle lists (basic)
  html = html.replace(/^\* (.+)$/gm, '<li>$1</li>')
  html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')

  // Handle links only if complete
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')

  // Sanitize the result
  const purifiedHtml = DOMPurify.sanitize(html)
  
  return purifiedHtml
}

async function saveDivAsImage(divId) {
  const element = document.getElementById(divId)

  // Get the full dimensions of the element
  const elementWidth = element.scrollWidth
  const elementHeight = element.scrollHeight

  return html2canvas(element, {
    scale: 1.5, // Adjust to control resolution
    width: elementWidth,
    height: elementHeight,
    windowWidth: elementWidth,
    windowHeight: elementHeight,
  }).then(canvas => {
    const imgData = canvas.toDataURL('image/png')
    return imgData
  })
}

function blobToFile(blob, filename) {
  // Create a new File object from the Blob
  return new File([blob], filename, { type: blob.type })
}

function base64ToFile(base64String, filename) {
  const arr = base64String.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], filename, { type: mime })
}

export {
  formatTimestamp,
  getCookieOptions,
  addTZtoDateTimeString,
  parseMarkup,
  parseStreamingMarkup,
  saveDivAsImage,
  blobToFile,
  base64ToFile,
}
